/**
 * Mirrors the backend's UserType enum
 * (packages/database/prisma/schema.prisma).
 *
 * UserType is coarse — it determines tenant scope and a few global
 * capabilities. The fine-grained persona separation from the Blueprint
 * (Compliance Manager, Officer, Department Staff, Auditor, etc.) is
 * enforced via the permissions/roles array surfaced separately.
 */
export type UserType =
  | 'PLATFORM_SUPER_ADMIN'
  | 'PLATFORM_ADMIN'
  | 'TENANT_SYSTEM_ADMIN'
  | 'TENANT_ADMIN'
  | 'TENANT_STAFF';

export interface AuthenticatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: UserType;
  tenantId: string | null;
  /** Active tenant subdomain. Powers demo-tenant-only UI gating
   *  (e.g. the role switcher). Null for platform-tier users. */
  tenantSubdomain: string | null;
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  /** Stable role keys held by the user (active non-expired). */
  roleKeys: string[];
  /** True when EVERY active role is readOnly — drives UI hide-write. */
  isReadOnly: boolean;
  /** True when AT LEAST ONE active role has isManager=true. */
  isManager: boolean;
}

/**
 * The 8 system role keys (kept in sync with the backend's
 * SYSTEM_ROLE_KEYS constant in tenant.constants.ts).
 */
export type SystemRoleKey =
  | 'TENANT_ADMIN'
  | 'COMPLIANCE_MANAGER'
  | 'COMPLIANCE_OFFICER'
  | 'RISK_MANAGER'
  | 'DEPARTMENT_MANAGER'
  | 'DEPARTMENT_STAFF'
  | 'AUDITOR_REVIEWER'
  | 'EXECUTIVE_VIEWER';

/**
 * Picks the dashboard the user should land on after sign-in. Order
 * matters: the first matching role wins. Platform users always
 * route to the super-admin surface.
 */
export function pickHomeRoute(user: AuthenticatedUser | null): string {
  if (!user) return '/auth/login';
  if (isPlatformUser(user)) return '/app';
  const keys = user.roleKeys ?? [];
  if (keys.includes('TENANT_ADMIN') || keys.includes('COMPLIANCE_MANAGER')) return '/app';
  if (keys.includes('RISK_MANAGER')) return '/app/risks';
  if (keys.includes('EXECUTIVE_VIEWER') || keys.includes('AUDITOR_REVIEWER')) return '/app/executive';
  if (keys.includes('DEPARTMENT_MANAGER')) return '/app';
  if (keys.includes('COMPLIANCE_OFFICER')) return '/app/me';
  if (keys.includes('DEPARTMENT_STAFF')) return '/app/me';
  return '/app';
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthenticatedUser;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export const PLATFORM_USER_TYPES: ReadonlySet<UserType> = new Set([
  'PLATFORM_SUPER_ADMIN',
  'PLATFORM_ADMIN',
]);

export function isPlatformUser(user: { userType: UserType } | null | undefined): boolean {
  return !!user && PLATFORM_USER_TYPES.has(user.userType);
}
